import { useMutation, useQuery } from '@tanstack/react-query';
import useApiClient from 'hooks/use-api-client';
import type { BlueprintApi } from '@env0/blueprint-service/api';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { getQueryClientInstance } from 'stores/rq/common/query-client-provider';

export type UpdateApprovalPolicyBody = BlueprintApi.UpdateApprovalPolicy.Request.Body & { id: string };

const KEY = 'approval-policies';

const useCacheKeys = () => {
  const organizationId = useCurrentOrganizationId();
  const baseKey = [organizationId, KEY] as const;
  return {
    all: [...baseKey, 'all'],
    single: (name: string) => [...baseKey, name],
    catalog: [...baseKey, 'catalog']
  } as const;
};

const useCommonHookSetup = () => {
  return {
    apiClient: useApiClient(),
    queryClient: getQueryClientInstance(),
    organizationId: useCurrentOrganizationId(),
    keys: useCacheKeys()
  };
};

export const useCreateApprovalPolicy = () => {
  const { apiClient, keys } = useCommonHookSetup();

  return useMutation({
    mutationFn: (data: { scopeId?: string } & BlueprintApi.CreateConfigurationTemplate) => {
      return apiClient.approvalPolicies.create(data);
    },
    onSuccess: (data: BlueprintApi.ApprovalPolicyTemplate) => {
      getQueryClientInstance().setQueryData(keys.single(data.name), data);
    }
  });
};

export const useDeleteApprovalPolicy = () => {
  const { apiClient, keys } = useCommonHookSetup();

  return useMutation({
    mutationFn: ({ id, withAssignments }: { id: string; withAssignments: boolean }) => {
      return apiClient.approvalPolicies.delete(id, { withAssignments: withAssignments ? 'true' : 'false' });
    },

    onSettled: () => {
      // Always refetch after error or success to ensure we have the correct data
      getQueryClientInstance().invalidateQueries({ queryKey: keys.all });
    }
  });
};

export const useUpdateApprovalPolicy = () => {
  const { apiClient, keys } = useCommonHookSetup();

  return useMutation({
    mutationFn: (data: UpdateApprovalPolicyBody) => apiClient.approvalPolicies.update(data),
    onSuccess: (data: BlueprintApi.ApprovalPolicyTemplate) => {
      getQueryClientInstance().setQueryData(keys.single(data.name), data);
      getQueryClientInstance().invalidateQueries({ queryKey: keys.all });
    }
  });
};

export const useGetPoliciesCatalog = () => {
  const { apiClient, keys } = useCommonHookSetup();

  return useQuery({
    queryKey: keys.catalog,
    queryFn: () => apiClient.approvalPolicies.getPolicyCatalog(),
    staleTime: Infinity,
    gcTime: Infinity
  });
};

export const useFindAllPolicies = () => {
  const { apiClient, keys, organizationId } = useCommonHookSetup();
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.all,
    queryFn: () => apiClient.approvalPolicies.findAllApprovalPolicies(organizationId)
  });
};
